import {passWindow} from '../utils/observations.js';
import { tierColor as passTierColor, ratingWord, PASS_WINDOW_HOURS } from '../utils/passTiers.js';

// Compact horizontal pass-picker for the mobile Tonight pane.
// Sits above the PassPlot; tapping a chip calls onFocus(pass) which flows
// back to App.jsx → setFocusedPass → activePass → MobilePaneTonight.pass.
//
// Only shows visible upcoming passes (tier > 0) in start-time order,
// mirroring the TonightStrip filter. Falls back to a single empty-state line.

const MONO = 'var(--mono)';

// "9:22p" compact clock
const fmtShort = ms => {
  const d = new Date(ms);
  let h = d.getHours();
  const m = d.getMinutes();
  const ap = h < 12 ? 'a' : 'p';
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, '0')}${ap}`;
};

const shortName = n => {
  const s = (n || '').replace(/\s*\(.*\)/, '');
  return s.length > 11 ? s.slice(0, 10) + '…' : s;
};

export default function MobilePassChips({ passes = [], focusedKey, onFocus }) {
  const now = Date.now();

  // Mirror the TonightStrip filter: tier > 0, upcoming within PASS_WINDOW_HOURS
  const chips = (passes || [])
    .filter(p => (p.visibility_tier || 0) > 0
      && new Date(p.start) > now
      && new Date(p.start) < now + PASS_WINDOW_HOURS * 3600e3)
    .sort((a, b) => new Date(a.start) - new Date(b.start));

  if (chips.length === 0) return null;   // PassPlot shows its own empty state

  return (
    <div style={{
      overflowX: 'auto',
      overflowY: 'hidden',
      WebkitOverflowScrolling: 'touch',
      scrollbarWidth: 'none',       // Firefox
      msOverflowStyle: 'none',      // IE/Edge
      padding: '6px 10px 8px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: 6,
      borderBottom: '1px solid var(--hair)',
    }}>
      {chips.map(p => {
        const key = `${p.norad_id}:${p.start}`;
        const selected = focusedKey === key;
        const tier = p.visibility_tier || 0;
        const color = passTierColor(tier) ?? passTierColor(0);
        const rating = ratingWord(p);
        const name = shortName(p.name);
        const time = fmtShort(new Date(p.start).getTime());
        const el = Math.round(p.max_el || 0);
        const mag = p.approx_magnitude != null ? `m${p.approx_magnitude.toFixed(1)}` : '';

        const tint = pct => `color-mix(in srgb, ${color} ${pct}%, transparent)`;

        return (
          <button
            key={key}
            onClick={() => onFocus?.(p)} aria-pressed={selected} aria-label={`${p.name} · ${passWindow(p)} · ${rating}`}
            style={{
              flexShrink: 0,
              background: selected ? color : tint(12),
              border: `1px solid ${selected ? color : tint(55)}`,
              borderRadius: 6,
              boxShadow: selected ? `0 0 10px ${tint(70)}` : 'none',
              padding: '5px 9px',
              cursor: 'pointer',
              textAlign: 'left',
              minWidth: 72,
              transition: 'background 120ms, box-shadow 120ms',
            }}
          >
            {/* Rating word */}
            <span style={{
              display: 'block',
              fontFamily: MONO, fontSize: 'var(--ui-small)', fontWeight: 700,
              letterSpacing: '0.12em',
              color: selected ? '#0e0e0e' : color,
              marginBottom: 2,
            }}>{rating}</span>

            {/* Satellite name */}
            <span style={{
              display: 'block',
              fontFamily: MONO, fontSize: 'var(--ui-label)', fontWeight: selected ? 800 : 600,
              color: selected ? '#0e0e0e' : 'var(--txt-bright)',
              letterSpacing: '0.02em',
              whiteSpace: 'nowrap',
            }}>{name}</span>

            {/* time · el · mag */}
            <span style={{
              display: 'block',
              fontFamily: MONO, fontSize: 'var(--ui-small)',
              color: selected ? '#0e0e0e' : 'var(--txt-dim)',
              marginTop: 2,
              whiteSpace: 'normal',maxWidth:240,
            }}>{passWindow(p)}<br />{el}°{mag ? ` · ${mag}` : ''}</span>
          </button>
        );
      })}
    </div>
  );
}
